// ── 직원 일괄등록 훅 — 스프레드시트 붙여넣기 등록 로직 ───────────────────────
import { useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import {
  BULK_COLUMNS, createEmptyBulkRow, isBulkRowEmpty,
  parseClipboardMatrix, normalizeBulkCellValue, getBulkCellError,
  normalizeDepartmentValue,
} from './utils';

/**
 * 일괄등록 모달 상태 + 액션 훅
 * @param {object} params
 *   showToast             — 토스트 알림 함수
 *   departments           — 부서 유효성 검사용 목록
 *   getDefaultAttendanceType — 기본 출근 유형 반환 함수
 *   fetchUsers            — 목록 새로 고침 콜백
 *   apiBase               — 일괄등록 API 기본 경로
 */
export function useBulkRegister({
  showToast,
  departments,
  getDefaultAttendanceType,
  fetchUsers,
  apiBase = '/api/admin/users',
  fixedDepartment = '',
  // 등록 주소를 통째로 줄 때(2026-10-26) — 운영자 초기설정의 마케터 일괄등록은 회사를 주소에 싣는다(작업 대상과 무관하게 그 회사로)
  bulkUrl = '',
}) {
  // 일괄등록 모달 열림 여부
  const [bulkOpen, setBulkOpen]             = useState(false);
  // 스프레드시트 행 데이터 (초기: 12개 빈 행)
  const [bulkRows, setBulkRows]             = useState(() => Array.from({ length: 12 }, createEmptyBulkRow));
  // 서버 전송 중 여부
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const bulkColumns = useMemo(
    () => (fixedDepartment ? BULK_COLUMNS.filter((col) => col.key !== 'department') : BULK_COLUMNS),
    [fixedDepartment],
  );

  // ── 유효성 오류 맵 (행 × 열) ──────────────────────────────────────────────
  const bulkErrors = useMemo(
    () => bulkRows.map((row) =>
      Object.fromEntries(bulkColumns.map((col) => [col.key, getBulkCellError(row, col.key, departments)]))
    ),
    [bulkRows, bulkColumns, departments]
  );

  // ── 비어있지 않은 행 수 ────────────────────────────────────────────────────
  const bulkNonEmptyCount = useMemo(
    () => bulkRows.filter((row) => !isBulkRowEmpty(row)).length,
    [bulkRows]
  );

  // ── 오류가 있는 행 수 ─────────────────────────────────────────────────────
  const bulkInvalidRowCount = useMemo(
    () => bulkRows.filter((row, rowIndex) =>
      !isBulkRowEmpty(row) && bulkColumns.some((col) => bulkErrors[rowIndex][col.key])
    ).length,
    [bulkRows, bulkErrors, bulkColumns]
  );

  // ── 행 추가 ───────────────────────────────────────────────────────────────
  function addBulkRows(count = 10) {
    setBulkRows((prev) => [...prev, ...Array.from({ length: count }, createEmptyBulkRow)]);
  }

  // ── 빈 행 정리 ───────────────────────────────────────────────────────────
  function trimEmptyBulkRows() {
    const filtered = bulkRows.filter((row) => !isBulkRowEmpty(row));
    setBulkRows(filtered.length > 0 ? filtered : [createEmptyBulkRow()]);
  }

  // ── 셀 값 변경 (단일 셀) ─────────────────────────────────────────────────
  function handleBulkCellChange(rowIndex, key, rawValue) {
    const normalized = normalizeBulkCellValue(key, rawValue);
    setBulkRows((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [key]: normalized };
      return next;
    });
  }

  // ── 셀 붙여넣기 (2차원 행렬 → 여러 셀에 분산) ───────────────────────────
  function handleBulkCellPaste(event, startRowIndex, startColIndex) {
    const text = event.clipboardData.getData('text/plain');
    const matrix = parseClipboardMatrix(text);
    if (matrix.length === 0) return;

    // 다중 셀 붙여넣기는 기본 동작 방지
    if (matrix.length > 1 || (matrix[0] && matrix[0].length > 1)) {
      event.preventDefault();
    }

    setBulkRows((prev) => {
      const next = [...prev];
      // 데이터가 넘치면 행 자동 추가
      const requiredRows = startRowIndex + matrix.length;
      while (next.length < requiredRows) next.push(createEmptyBulkRow());

      matrix.forEach((rowValues, rowOffset) => {
        rowValues.forEach((cellValue, colOffset) => {
          const col = bulkColumns[startColIndex + colOffset];
          if (!col) return;
          const targetRowIndex = startRowIndex + rowOffset;
          next[targetRowIndex] = {
            ...next[targetRowIndex],
            [col.key]: normalizeBulkCellValue(col.key, cellValue),
          };
        });
      });

      return next;
    });
  }

  // ── 일괄등록 실행 ────────────────────────────────────────────────────────
  async function submitBulk() {
    const nonEmptyRows = bulkRows.filter((row) => !isBulkRowEmpty(row));
    if (nonEmptyRows.length === 0) {
      showToast('붙여넣을 데이터가 없습니다.', 'error');
      return;
    }

    const hasInvalid = bulkRows.some(
      (row, rowIndex) =>
        !isBulkRowEmpty(row) && bulkColumns.some((col) => Boolean(bulkErrors[rowIndex][col.key]))
    );
    if (hasInvalid) {
      showToast('빨간색 셀의 값을 수정한 뒤 다시 시도해 주세요.', 'error');
      return;
    }

    const payloadRows = nonEmptyRows.map((row) => ({
      name:                 row.name.trim(),
      birth_date:           row.birth_date || null,
      address:              row.address || null,
      phone:                row.phone || null,
      hire_date:            row.hire_date || null,
      department:           fixedDepartment || normalizeDepartmentValue(row.department),
      attendance_time_type: getDefaultAttendanceType(),
      email:                row.email ? (row.email.includes('@') ? row.email : `${row.email}@gmail.com`) : null,
      bank_name:            row.bank_name || null,
      bank_account:         row.bank_account || null,
      resign_date:          row.resign_date || null,
      memo:                 row.memo || null,
    }));

    setBulkSubmitting(true);
    const result = await apiClient.post(bulkUrl || `${apiBase}/bulk`, { rows: payloadRows });
    setBulkSubmitting(false);

    if (!result.ok) {
      showToast(result.data?.message || '일괄 등록에 실패했습니다.', 'error');
      return;
    }

    showToast(`${payloadRows.length}명을 등록했습니다.`, 'success');
    setBulkOpen(false);
    setBulkRows(Array.from({ length: 12 }, createEmptyBulkRow));
    fetchUsers?.();
  }

  // ── 모달 닫기 & 상태 초기화 ─────────────────────────────────────────────
  function closeBulkModal() {
    setBulkOpen(false);
    setBulkSubmitting(false);
    setBulkRows(Array.from({ length: 12 }, createEmptyBulkRow));
  }

  return {
    bulkOpen, setBulkOpen,
    bulkRows, bulkErrors, bulkColumns, bulkNonEmptyCount, bulkInvalidRowCount,
    bulkSubmitting,
    addBulkRows, trimEmptyBulkRows,
    handleBulkCellChange, handleBulkCellPaste,
    submitBulk, closeBulkModal,
  };
}
